import React, {Suspense} from 'react';
import {Route, Switch} from 'react-router-dom';
import {useUserRole} from '../../contexts/userRoleContext';
import {withRoleProtection} from '../ProtectedRoute/ProtectedRoute';
import {ROUTE_CONFIG, getAccessibleRoutes} from '../../config/routeConfig';

/**
 * Component that renders routes with role-based protection
 */
export default function ProtectedRoutes() {
  const userRole = useUserRole();

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Switch>
        {ROUTE_CONFIG.map(route => {
          const {path, component: Component, exact, protection} = route;

          return (
            <Route
              key={path}
              exact={exact}
              path={path}
              render={props => {
                // Check if user can access this route
                const accessibleRoutes = getAccessibleRoutes(userRole);
                const canAccess = accessibleRoutes.some(r => r.path === path);

                if (!canAccess) {
                  return (
                    <div style={{padding: '2rem', textAlign: 'center'}}>
                      <h2>Access Denied</h2>
                      <p>You don't have permission to access this page.</p>
                    </div>
                  );
                }

                // Automatically apply role protection based on route config
                if (protection && Object.keys(protection).length > 0) {
                  const ProtectedComponent = withRoleProtection(protection)(Component);
                  return <ProtectedComponent {...props} />;
                }

                // No protection needed, render component directly
                return <Component {...props} />;
              }}
            />
          );
        })}

        {/* Fallback route for unknown paths */}
        <Route
          path="*"
          component={() => (
            <div style={{padding: '2rem', textAlign: 'center'}}>
              <h2>Page Not Found</h2>
              <p>The page you're looking for doesn't exist.</p>
            </div>
          )}
        />
      </Switch>
    </Suspense>
  );
}

/**
 * Alternative implementation using render prop for more control
 */
export function ProtectedRoutesWithRender() {
  const userRole = useUserRole();
  const accessibleRoutes = getAccessibleRoutes(userRole);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Switch>
        {ROUTE_CONFIG.map(route => {
          const {path, component: Component, exact, protection} = route;

          return (
            <Route
              key={path}
              exact={exact}
              path={path}
              render={props => {
                // Check if user can access this route
                const canAccess = accessibleRoutes.some(r => r.path === path);

                if (!canAccess) {
                  return (
                    <div style={{padding: '2rem', textAlign: 'center'}}>
                      <h2>Access Denied</h2>
                      <p>You don't have permission to access this page.</p>
                    </div>
                  );
                }

                // Apply role protection if specified
                if (protection && Object.keys(protection).length > 0) {
                  const ProtectedComponent = withRoleProtection(protection)(Component);
                  return <ProtectedComponent {...props} />;
                }

                return <Component {...props} />;
              }}
            />
          );
        })}

        {/* Fallback route */}
        <Route
          path="*"
          component={() => (
            <div style={{padding: '2rem', textAlign: 'center'}}>
              <h2>Page Not Found</h2>
              <p>The page you're looking for doesn't exist.</p>
            </div>
          )}
        />
      </Switch>
    </Suspense>
  );
}
